function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useState } from 'react';
import { compact } from 'lodash-es';
import { Controller, useForm } from 'react-hook-form';
import { nameValidation } from 'validators';
import { FunctionalLoader, TextField, ConfirmationModal, Grid, GridItem, MenuItem, Select, Typography } from 'seagull';
import { validateContact } from "../../business-logic/validators";
import { ResponsiveModal } from "../../client-flow/components/ResponsiveModal";
import { useResponsive } from "../../client-flow/contexts";
import { ErrorText } from "../../constants";
import { useEventReporter, useOverlayContext, useStore, useStoreActions } from "../../contexts";
import { useOnMount } from "../../hooks/useOnMount";
import { ErrorResponse, ServerError } from "../../infra/ErrorResponse";
import { handlePromise } from "../../infra/utils";
import { UserRole } from "../../models";
import { selectors } from "../../store/state-selectors";
import { connectControllerToTextField } from "../../utils/react-hook-form.utils";
import styles from "./InviteOrEditMemberModal.module.scss";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const testPrefix = 'invite-member-modal';
const defaultRole = UserRole.Member;
export default function InviteOrEditMemberModal(props) {
  const {
    isResponsive,
    member,
    allMembers,
    onClose,
    onSuccess,
    skin = 'primary'
  } = props;
  const overlayContext = useOverlayContext();
  const worksAt = useStore(selectors.user.business);
  const userRole = useStore(selectors.user.role);
  const isVendor = useStore(selectors.user.isVendor);
  const userId = useStore(selectors.user.id);
  const {
    setWorksAt,
    api,
    logBI,
    fetchUserInfo
  } = useStoreActions();
  const {
    reportFormSubmission,
    prepareFormObject
  } = useEventReporter();
  const isEditMode = !!member;
  const {
    control,
    formState,
    handleSubmit
  } = useForm({
    mode: 'all',
    defaultValues: isEditMode ? {
      firstName: member.firstName(),
      lastName: member.lastName(),
      email: member.email(),
      role: member.effectiveRole()
    } : {
      firstName: '',
      lastName: '',
      email: '',
      role: ''
    }
  });
  const {
    isDesktop
  } = useResponsive();
  const [roleDropdownEl, setRoleDropdownEl] = useState(null);
  const [existingMembers, setExistingMembers] = useState(allMembers);
  const firstNameValidator = nameValidation('First name', {
    required: 'Please enter a first name'
  });
  const lastNameValidator = nameValidation('Last name', {
    required: 'Please enter a last name'
  });
  const roleValidator = nameValidation('Role', {
    required: 'Please enter a role'
  });
  useOnMount(() => {
    async function retrieveMembers() {
      try {
        const {
          members
        } = await api.member.getAll();
        setExistingMembers(members);
      } catch (err) {
        overlayContext.showErrorToast(ErrorText.UnexpectedErrorTryAgain, 'failed-to-load-members-toast');
        onClose();
      }
    }
    if (!existingMembers) {
      // relevant in the client onboarding and payment method tab
      retrieveMembers();
    }
  });
  const onValidSubmit = async _ref => {
    let {
      firstName,
      lastName,
      email,
      role
    } = _ref;
    if (isEditMode) {
      const isEditingCurrentUser = member.id() === userId;
      const payload = {
        id: member.id(),
        currentRole: member.effectiveRole(),
        newRole: role || defaultRole
      };
      logBI(12200, _objectSpread(_objectSpread({}, payload), {}, {
        email: member.email()
      }));
      const changeRoleLoader = 'changeRole';
      overlayContext.startLoader(changeRoleLoader);
      const result = await handlePromise(api.member.changeRole(payload));
      if (!result.success) {
        overlayContext.stopLoader(changeRoleLoader);
        return overlayContext.showErrorToast('Failed to change role', 'cr-fail');
      }
      if (isEditingCurrentUser) {
        await handlePromise(fetchUserInfo());
      }
      const userChangedHimselfToMember = isEditingCurrentUser && role === UserRole.Member;
      if (!userChangedHimselfToMember) {
        // When a user changes his own role to member he's no longer allowed to access member APIs (which is what onSuccess does).
        onSuccess && (await onSuccess());
      }
      overlayContext.stopLoader(changeRoleLoader);
      onClose();
    } else {
      try {
        const invitedRole = role || defaultRole;
        logBI(12201, {
          email,
          role: invitedRole
        });
        await api.member.invite({
          firstName,
          lastName,
          email,
          role: invitedRole
        });
        if (worksAt) {
          setWorksAt(_objectSpread(_objectSpread({}, worksAt), {}, {
            membersCount: worksAt.membersCount + 1
          }));
        }
        reportFormSubmission('team-invited-member', [prepareFormObject('email', email), prepareFormObject('firstName', firstName), prepareFormObject('lastName', lastName)]);
        onSuccess && (await onSuccess());
        overlayContext.showSuccessToast("Invite sent to ".concat(email), 'sent-invite-succsessfully-toast');
      } catch (err) {
        const e = new ErrorResponse(err);
        const text = e.hasServerError(ServerError.PendingInvitationAlreadyExists) ? 'There is already a pending invitation for this email' : e.hasServerError([ServerError.UserAlreadyAffiliatedWithBusiness, ServerError.UserAlreadyInBusiness]) ? 'This email already belongs to a member of your team' : ErrorText.UnexpectedErrorTryAgain;
        overlayContext.showErrorToast(text);
      } finally {
        onClose();
      }
    }
  };
  const emailRules = () => ({
    validate: {
      email: validateContact.email,
      emailAlreadyUsed: email => (existingMembers === null || existingMembers === void 0 ? void 0 : existingMembers.every(m => m.email() !== email)) || 'This email already belongs to a member of your team'
    }
  });
  const roleOptions = compact([{
    value: UserRole.Owner,
    text: 'Owner',
    subtext: 'Has access to everything',
    disabled: userRole !== UserRole.Owner && userRole !== UserRole.AnchorAdmin
  }, isVendor && {
    value: UserRole.Admin,
    text: 'Admin',
    subtext: 'Has access to everything except for changing your bank account'
  }, {
    value: UserRole.Member,
    text: 'Member',
    subtext: isVendor ? 'Has access to everything needed to manage a client but does not have access to your business settings or payouts' : 'Can view everything but cannot make any changes except for adding a payment method.'
  }]);
  const commonModalProps = {
    skin,
    ['data-testid']: testPrefix,
    title: "".concat(isEditMode ? 'Edit' : 'Invite', " a team member"),
    onClose,
    primaryButtonLabel: isEditMode ? 'Save' : 'Send Invitation',
    primaryButtonDisabled: formState.isSubmitting || isEditMode && !formState.isDirty,
    onPrimaryButtonClick: handleSubmit(onValidSubmit),
    secondaryButtonLabel: 'Cancel',
    onSecondaryButtonClick: onClose
  };
  const modalContent = /*#__PURE__*/_jsxs(Grid, {
    className: styles.modalContent,
    children: [/*#__PURE__*/_jsx(GridItem, {
      span: isDesktop ? 6 : 12,
      children: /*#__PURE__*/_jsx(Controller, {
        control: control,
        name: "firstName",
        rules: firstNameValidator,
        render: renderProps => /*#__PURE__*/_jsx(TextField, _objectSpread(_objectSpread({
          collapseHelperText: false,
          disabled: isEditMode,
          label: "First name",
          "data-testid": "".concat(testPrefix, "-input-first-name")
        }, connectControllerToTextField(renderProps)), firstNameValidator.dataAttr))
      })
    }), /*#__PURE__*/_jsx(GridItem, {
      span: isDesktop ? 6 : 12,
      children: /*#__PURE__*/_jsx(Controller, {
        control: control,
        name: "lastName",
        rules: lastNameValidator,
        render: renderProps => /*#__PURE__*/_jsx(TextField, _objectSpread(_objectSpread({
          collapseHelperText: false,
          disabled: isEditMode,
          label: "Last name",
          "data-testid": "".concat(testPrefix, "-input-last-name")
        }, connectControllerToTextField(renderProps)), lastNameValidator.dataAttr))
      })
    }), /*#__PURE__*/_jsx(GridItem, {
      span: 12,
      children: /*#__PURE__*/_jsx(Controller, {
        control: control,
        name: "email",
        rules: isEditMode ? undefined : emailRules(),
        render: renderProps => /*#__PURE__*/_jsx(TextField, _objectSpread({
          collapseHelperText: false,
          disabled: isEditMode,
          label: "Email",
          "data-testid": "".concat(testPrefix, "-input-email")
        }, connectControllerToTextField(renderProps)))
      })
    }), /*#__PURE__*/_jsx(GridItem, {
      span: 12,
      children: /*#__PURE__*/_jsx(Controller, {
        control: control,
        name: "role",
        rules: roleValidator,
        render: p => /*#__PURE__*/_jsx(Select, _objectSpread(_objectSpread({
          collapseHelperText: false,
          label: "Role",
          ref: setRoleDropdownEl,
          "data-testid": "".concat(testPrefix, "-input-role"),
          placeholder: "Select a role"
        }, connectControllerToTextField(p)), {}, {
          children: roleOptions.map(_ref2 => {
            let {
              value,
              text,
              subtext,
              disabled
            } = _ref2;
            return /*#__PURE__*/_jsxs(MenuItem, {
              variant: "multiline",
              value: value,
              disabled: disabled,
              label: text,
              "data-testid": "".concat(testPrefix, "-role-opt-").concat(value),
              style: {
                width: roleDropdownEl === null || roleDropdownEl === void 0 ? void 0 : roleDropdownEl.clientWidth
              },
              children: [/*#__PURE__*/_jsx(Typography, {
                weight: "normal",
                children: text
              }), /*#__PURE__*/_jsx(Typography, {
                children: subtext
              })]
            }, value);
          })
        }))
      })
    })]
  });
  if (!existingMembers) {
    return /*#__PURE__*/_jsx(FunctionalLoader, {
      show: true,
      name: "fetch-members-loader"
    });
  }
  return isResponsive ? /*#__PURE__*/_jsx(ResponsiveModal, _objectSpread(_objectSpread({
    isDesktop: isDesktop,
    backTitle: "Back to agreement"
  }, commonModalProps), {}, {
    children: modalContent
  })) : /*#__PURE__*/_jsx(ConfirmationModal, _objectSpread(_objectSpread({}, commonModalProps), {}, {
    textAlignCenter: false,
    content: modalContent
  }));
}